import { memo, type SyntheticEvent } from "react";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import TextField from "@mui/material/TextField";
import Collapse from '@mui/material/Collapse';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getFormInputProps } from "util/forms";
import { getOptionName, isOptionEqualToValue, Option } from "util/option";
import { compareIds, isInIframe, when } from "util/support";
import Autocomplete from "ui/atoms/Autocomplete";
import { PasswordTextField } from "ui/atoms/TextField";
import { EMIPasswordTextField } from "ui/molecules/Input";
import { FormField, type PreApplicationProps } from "./types";
import { BusinessActivityOption } from "./BusinessActivityOption";
import Typography from "@mui/material/Typography";
import { WebRoutes } from "consts/enpoints/web";
import { DOMAIN } from "consts/general";
import { Button } from "@mui/material";

const PreApplication = ({
    sx,
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: PreApplicationProps) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    const resetField = (field: FormField) => () => {
        setFieldValue(field, '', true);
        setFieldTouched(field, false);
    };
    const navigateLogin = () => (
        when(
            isInIframe,
            () => (
                window.parent.postMessage({
                    type: 'CHILD_REDIRECT',
                    payload: {
                        href: `${window.location.origin}${WebRoutes.Login.Index}`
                    },
                }, `https://${DOMAIN}`)
            )
        )
    );


    return (
        <Box
            sx={{
                display: 'grid',
                gap: 2,
                ...sx
            }}
            component='form'
            onSubmit={handleSubmit}
            autoComplete='off'
        >
            <Autocomplete
                fullWidth
                disableClearable
                loading={isBootstrapDataLoading}
                size='small'
                options={bootstrapData.businessActivities}
                value={values[FormField.BusinessActivity]}
                getOptionLabel={getOptionName}
                isOptionEqualToValue={isOptionEqualToValue}
                onChange={onAutocompleteChange(FormField.BusinessActivity)}
                TextFieldProps={{
                    ...getFormInputProps(FormField.BusinessActivity, form),
                    required: true,
                    name: FormField.BusinessActivity,
                    label: 'Select your business activity'
                }}
            />
            <Autocomplete
                fullWidth
                disableClearable
                disabled={!values[FormField.BusinessActivity]}
                loading={isBootstrapDataLoading}
                size='small'
                options={BusinessActivityOption.appendWithOption(
                    values[FormField.BusinessActivity]?.productsAndServices,
                    Option.make(BusinessActivityOption.Other, 'Other')
                )}
                value={values[FormField.ProductService]}
                getOptionLabel={getOptionName}
                isOptionEqualToValue={isOptionEqualToValue}
                onChange={onAutocompleteChange(FormField.ProductService)}
                TextFieldProps={{
                    ...getFormInputProps(FormField.ProductService, form),
                    required: true,
                    name: FormField.ProductService,
                    placeholder: 'Start typing…',
                    label: 'Select the products/services you offer'
                }}
            />
            <Collapse
                unmountOnExit
                onExited={resetField(FormField.ProductServiceOther)}
                in={compareIds(
                    `${values[FormField.ProductService]?.id}`,
                    BusinessActivityOption.Other
                )}
            >
                <TextField
                    {...getFormInputProps(FormField.ProductServiceOther, form)}
                    multiline
                    required
                    fullWidth
                    rows={3}
                    size='small'
                    label='If other, please specify'
                    name={FormField.ProductServiceOther}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values[FormField.ProductServiceOther]}
                />
            </Collapse>
            <TextField
                {...getFormInputProps(FormField.Email, form)}
                required
                fullWidth
                size='small'
                label='Email'
                type='email'
                name={FormField.Email}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values[FormField.Email]}
            />
            <EMIPasswordTextField
                {...getFormInputProps(FormField.Password, form)}
                size='small'
                label='Password'
                name={FormField.Password}
                onChange={handleChange}
                onBlur={handleBlur}
            />
            <PasswordTextField
                {...getFormInputProps(FormField.ConfirmPassword, form)}
                size='small'
                label='Confirm Password'
                name={FormField.ConfirmPassword}
                onChange={handleChange}
                onBlur={handleBlur}
            />
            <LoadingButton
                fullWidth
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isValid}
                loading={isSubmitting}
                endIcon={(
                    <ArrowForwardIcon />
                )}
            >
                Create Account
            </LoadingButton>

            <Typography marginTop={2} variant='body2'>
                Already have an account?
                <Button
                    fullWidth
                    variant='contained'
                    color='success'
                    onClick={navigateLogin}

                    sx={{
                        marginTop: '10px'
                    }}>
                    Go to login page
                </Button>
            </Typography>
        </Box>
    );
};

export default memo(PreApplication);
